import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const intervalRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "expired") {
          clearInterval(intervalRef.current);
        }
      } catch (err) {
        console.error("Payment status poll failed", err);
      }
      setAttempts((a) => a + 1);
    };
    poll();
    intervalRef.current = setInterval(poll, 2000);
    const timeout = setTimeout(() => clearInterval(intervalRef.current), 60000);
    return () => { clearInterval(intervalRef.current); clearTimeout(timeout); };
  }, [sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-24 text-center">
        {paid ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#E6B860] mx-auto mb-6" />
            <div className="chip mx-auto mb-4 w-fit">Confirmed</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F7F3EC] tracking-tight mb-4">
              {status.kind === "donation" ? "Your tip landed." : "The vault is open."}
            </h1>
            <p className="font-serif-body text-[#A8A095] mb-8">
              {status.kind === "donation"
                ? "The author's wallet has been credited anonymously. They'll never know it was you."
                : "You've unlocked the full manuscript. Read it, keep it, or return whenever the story pulls you back."}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              {status.work_id && (
                <Link to={`/work/${status.work_id}`} className="btn-buy px-6 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2" data-testid="payment-view-work">
                  {status.kind === "donation" ? "Return to work" : "Read manuscript"} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
              <Link to="/browse" className="btn-ghost-gold px-6 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest" data-testid="payment-continue-browsing">
                Continue browsing
              </Link>
            </div>
          </>
        ) : status.payment_status === "failed" || status.payment_status === "expired" ? (
          <>
            <h1 className="font-serif-display text-4xl text-[#F7F3EC] mb-4">Payment did not complete.</h1>
            <p className="text-[#A8A095] mb-8">Please try again from the manuscript page.</p>
            <button onClick={() => nav("/browse")} className="btn-buy px-6 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold">Back to archive</button>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-[#D9B472] mx-auto mb-6 animate-spin" />
            <h1 className="font-serif-display text-3xl text-[#F7F3EC] mb-3">Sealing your transaction…</h1>
            <p className="text-[#A8A095]">Confirming with Stripe. Attempt {attempts}.</p>
          </>
        )}
      </div>
    </div>
  );
}
